import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Flex,
  Heading,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import PlanForm from './PlanForm';
import { Link } from 'react-router-dom';
import { getBackendURL, apiFetch } from '../../../utils/utils';
import { useSelector } from 'react-redux';

function MerchantPlanPage() {
  const user = useSelector(state => state.userState.user);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef();

  const [plan, setPlan] = useState({});
  const [error, setError] = useState('');
  const [isRequestCompleted, setIsRequestCompleted] = useState(false);
  const [inProgress, setInProgress] = useState(false);
  const [actionStatus, setActionStatus] = useState('');

  const [plans, setPlans] = useState([]);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = () => {
    apiFetch(
      `${getBackendURL()}/admin/merchant/plans?ownerId=${user._id}`
    )
      .then(response => response.json())
      .then(response => {
        if (response !== null && response.plans.length > 0) {
          let plans = [];
          response.plans.forEach(plan => {
            plans.push({
              id: plan._id,
              name: plan.name,
              price: plan.price,
              numberOfMonths: plan.numberOfMonths,
            });
          });
          setPlans(plans);
        } else {
          setPlans([]);
        }
      })
      .catch(error => {
        console.log(error);
        setPlans([]);
      });
  };

  const onPlanAdded = () => {
    fetchPlans();
  };

  const onDelete = index => {
    let planToRemove = plans[index];
    setPlan(planToRemove);
    setInProgress(false);
    setIsRequestCompleted(false);
    setActionStatus('');
    setError('');
    onOpen();
  };

  const onConfirmDelete = () => {
    setInProgress(true);
    apiFetch(`${getBackendURL()}/admin/merchant/plans/delete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ planId: plan.id, ownerId: user._id }),
    })
      .then(response => response.json())
      .then(response => {
        setIsRequestCompleted(true);
        setInProgress(false);
        if (response.plan) {
          setActionStatus(response.message);
        } else {
          setError(response.message);
        }
        fetchPlans();
      })
      .catch(error => {
        setIsRequestCompleted(true);
        setInProgress(false);
        setError(error);
        fetchPlans();
      });
  };

  return (
    <>
      <Breadcrumb marginTop={2} marginLeft={2}>
        <BreadcrumbItem>
          <BreadcrumbLink as={Link} to="/">
            Home
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={Link} to="/merchants">
            Merchants
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={Link} to="/merchants/plans">
            Plans
          </BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>
      <Stack mt={4} direction={{ base: 'column', lg: 'row' }}>
        <Flex flex={0.7}>
          <Box w={'100%'}>
            <Card bg={useColorModeValue('white', 'gray.700')}>
              <CardHeader>
                <Heading size="md">Plans</Heading>
              </CardHeader>
              <Divider />
              <CardBody>
                <TableContainer data-testid="plans-table">
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Sr. No</Th>
                        <Th>Name</Th>
                        <Th>Price</Th>
                        <Th>Number of months</Th>
                        <Th>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {plans.map((plan, index) => {
                        return (
                          <Tr key={plan.name}>
                            <Td>{index + 1}</Td>
                            <Td>{plan.name}</Td>
                            <Td isNumeric>{plan.price}</Td>
                            <Td isNumeric>{plan.numberOfMonths}</Td>
                            <Td>
                              <Stack direction="row">
                                <Button
                                  colorScheme="teal"
                                  variant="outline"
                                  onClick={() => onDelete(index)}
                                >
                                  Delete
                                </Button>
                              </Stack>
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </TableContainer>
              </CardBody>
            </Card>
          </Box>
        </Flex>
        <Flex flex={0.3}>
          <Box w={'100%'}>
            <PlanForm onPlanAdded={onPlanAdded} />
          </Box>
        </Flex>
      </Stack>
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        closeOnOverlayClick={false}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Plan
            </AlertDialogHeader>
            {inProgress ? (
              <Box ml={'auto'} mr={'auto'} mt={3} mb={3}>
                <Spinner
                  thickness="4px"
                  speed="0.65s"
                  emptyColor="gray.200"
                  color="blue.500"
                  size="xl"
                />
              </Box>
            ) : (
              <>
                {isRequestCompleted ? (
                  <>
                    <AlertDialogBody m={3}>
                      <Alert
                        status="success"
                        display={actionStatus ? 'flex' : 'none'}
                      >
                        <AlertIcon />
                        {actionStatus}
                      </Alert>
                      <Alert status="error" display={error ? 'flex' : 'none'}>
                        <AlertIcon />
                        {error}
                      </Alert>
                    </AlertDialogBody>
                    <AlertDialogFooter justifyContent={'center'}>
                      <Button ref={cancelRef} onClick={onClose}>
                        {error ? 'Retry' : 'Go back'}
                      </Button>
                    </AlertDialogFooter>
                  </>
                ) : (
                  <>
                    <AlertDialogBody>
                      Are you sure? You can't delete this plan
                    </AlertDialogBody>
                    <AlertDialogFooter>
                      <Button ref={cancelRef} onClick={onClose}>
                        Cancel
                      </Button>
                      <Button
                        colorScheme="red"
                        onClick={onConfirmDelete}
                        ml={3}
                      >
                        Delete
                      </Button>
                    </AlertDialogFooter>
                  </>
                )}
              </>
            )}
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}

export default MerchantPlanPage;
